import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as React from "react"
import { Checkbox, Form, Input, InputNumber, Radio } from "antd"
import { WrappedFormUtils } from "antd/lib/form/Form"
import { renderRadioButtonGroupFormItem } from "../../../screens/settings/_components/SettingFormItems"
import { SettingFormItem } from "../../../screens/settings/types"
import { getTippingPercentageKey, TIP_PERCENTAGES_COUNT, TipMethodEnum } from "./utils"
import { last } from "ramda"

type Props = {
    hideDisabledOption?: boolean // Quickhack: We are not showing 'Disabled' tipping method in MIB settings
    settings: SettingFormItem
    formKeys: {
        tipMethod: string
        tipMethodPercentages: string
        preselectedTip: string
    }
    form: WrappedFormUtils
    error?: Maybe<string>
    onChangeCallback: () => void
}

export const TipMethodFormComponent = ({ hideDisabledOption = false, ...props }: Props) => {
    const tipMethodValue: Maybe<TipMethodEnum> = props.form.getFieldValue(props.formKeys.tipMethod)
    return (
        <div>
            {renderRadioButtonGroupFormItem(
                props.settings,
                props.formKeys.tipMethod,
                "",
                props.form,
                null,
                props.onChangeCallback,
                "radio-align-left",
                [
                    ...(!hideDisabledOption
                        ? [
                              <div key={TipMethodEnum.Disabled} className="pb-4">
                                  <Radio className={classes.radioItem} value={TipMethodEnum.Disabled}>
                                      Do Not Accept Tips with Card Payment
                                  </Radio>
                                  <div className={classes.radioDesc}>
                                      Not accepting tips with card payments on Register and Kiosk
                                  </div>
                              </div>,
                          ]
                        : []),
                    <div key={TipMethodEnum.Simple} className="pb-4">
                        <Radio className={classes.radioItem} value={TipMethodEnum.Simple}>
                            Simple Tip Guide
                        </Radio>
                        <div className={classes.radioDesc}>
                            Define percentages which will be offered to customers as pre-defined tips
                        </div>
                    </div>,
                    <div key={TipMethodEnum.Smart}>
                        <Radio className={classes.radioItem} value={TipMethodEnum.Smart}>
                            &quot;Smart&quot; Tip Guide
                        </Radio>
                        <div className={classes.radioDesc}>
                            On orders below $10, fixed tips of $1, $2 or $3 are offered.
                            <br />
                            Orders above $10 respect defined percentages.
                        </div>
                    </div>,
                ]
            )}
            {tipMethodValue !== TipMethodEnum.Disabled && (
                <div className={classes.bottomDiv}>
                    <div className="flex-1">
                        {tipMethodValue === TipMethodEnum.Smart && <div>&quot;Smart&quot; Tip Guide Percentages</div>}
                        {tipMethodValue === TipMethodEnum.Simple && <div>Simple Tip Guide Percentages</div>}
                        {tipMethodValue === TipMethodEnum.Simple && (
                            <div className={classes.tipMethodDesc}>
                                Maximum 4 tip options.
                                <br />
                                Customer can always define custom tip.
                            </div>
                        )}
                        {tipMethodValue === TipMethodEnum.Smart && (
                            <div className={classes.tipMethodDesc}>
                                These percentages will be offered only on orders above $10.
                                <br />
                                Customer can always define custom tip.
                            </div>
                        )}
                    </div>
                    <PreselectedCheckboxes
                        tipMethodPercentagesKey={props.formKeys.tipMethodPercentages}
                        preselectedTipKey={props.formKeys.preselectedTip}
                        form={props.form}
                        settings={props.settings}
                        onChangeCallback={props.onChangeCallback}
                    />
                    <div className="flex flex-col text-right">
                        <div className={classes.inputsTitle}>Tip Option(s)</div>
                        <div className={classes.percentageRowItem}>
                            <Input
                                readOnly={true}
                                value="No Tip"
                                className="pointer-events-none border-dashed text-right"
                            />
                        </div>
                        {[...Array(TIP_PERCENTAGES_COUNT)].map((_, index) => {
                            return (
                                <div key={index} className={classes.percentageRowItem}>
                                    <TipPercentageInput
                                        index={index}
                                        settings={props.settings}
                                        form={props.form}
                                        tipMethodPercentagesKey={props.formKeys.tipMethodPercentages}
                                        preselectedTipKey={props.formKeys.preselectedTip}
                                        onChangeCallback={props.onChangeCallback}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {props.error && <div className={classes.error}>{props.error}</div>}
        </div>
    )
}

type PreselectedCheckboxesProps = {
    settings: SettingFormItem
    form: WrappedFormUtils
    preselectedTipKey: string
    tipMethodPercentagesKey: string
    onChangeCallback: () => void
}

const PreselectedCheckboxes = (props: PreselectedCheckboxesProps) => {
    const settingValue = props.settings[props.preselectedTipKey].value

    return (
        <div>
            {props.form.getFieldDecorator(props.preselectedTipKey, {
                initialValue: settingValue,
                getValueFromEvent: value => last(value) ?? null,
                getValueProps: value => {
                    // If value is 'indeterminate'
                    if (value === undefined) {
                        return {
                            value: undefined,
                        }
                    }
                    return {
                        value: value != null ? [value] : [],
                    }
                },
            })(
                <Checkbox.Group
                    className="flex flex-col items-center px-5"
                    onChange={() => {
                        props.onChangeCallback()
                    }}>
                    <div className={classes.inputsTitle}>Default</div>
                    <div className={classes.checkboxRowItem}>
                        <Checkbox value={0} style={{ margin: 0 }} />
                    </div>
                    {[...Array(TIP_PERCENTAGES_COUNT)].map((_, index) => {
                        const percentagesSettingValue = props.settings[props.tipMethodPercentagesKey].value
                        const formInputValue = percentagesSettingValue
                            ? percentagesSettingValue.split(",")[index]
                            : null

                        const hasValue = !!(
                            props.form.getFieldValue(getTippingPercentageKey(index, props.tipMethodPercentagesKey)) ||
                            formInputValue
                        )
                        return (
                            <div className={classes.checkboxRowItem} key={index}>
                                <Checkbox value={index + 1} style={{ margin: 0 }} disabled={!hasValue} />
                            </div>
                        )
                    })}
                </Checkbox.Group>
            )}
        </div>
    )
}

type TipPercentageProps = {
    index: number
    settings: SettingFormItem
    form: WrappedFormUtils
    tipMethodPercentagesKey: string
    preselectedTipKey: string
    onChangeCallback: () => void
}

const TipPercentageInput = (props: TipPercentageProps) => {
    const formKey = getTippingPercentageKey(props.index, props.tipMethodPercentagesKey)
    const percentagesSettingValue = props.settings[props.tipMethodPercentagesKey].value
    const formInputValue = percentagesSettingValue ? percentagesSettingValue.split(",")[props.index] : null
    const options = {
        initialValue:
            (props.form.getFieldValue(formKey) ? Number(props.form.getFieldValue(formKey)) / 1000 : 0) || formInputValue
                ? Number(formInputValue) / 1000
                : undefined,
    }

    return (
        <Form.Item className="mb-1">
            {props.form.getFieldDecorator(
                formKey,
                options
            )(
                <InputNumber
                    className={classes.percentageInput}
                    min={0}
                    formatter={value => (value ? value + "%" : "")}
                    onFocus={e => e.target.select()}
                    parser={value => (value || "").replace("%", "")}
                    onChange={val => {
                        props.form.setFieldsValue({
                            [formKey]: val ? val * 1000 : null,
                        })

                        props.onChangeCallback()
                    }}
                    onBlur={() => {
                        // If the field value is 0 or is empty, reset the field value
                        // + also uncheck the 'prepselected tip' checkbox
                        if (!props.form.getFieldValue(formKey)) {
                            const isPreselected = props.form.getFieldValue(props.preselectedTipKey) === props.index + 1
                            props.form.setFieldsValue({
                                [formKey]: null,
                                ...(isPreselected ? { [props.preselectedTipKey]: null } : null),
                            })
                        }
                    }}
                    placeholder={
                        props.settings[props.tipMethodPercentagesKey].indeterminate ? "Multiple values" : "Enter value"
                    }
                />
            )}
        </Form.Item>
    )
}

const classes = {
    radioItem: "block",
    radioDesc: "relative text-12 leading-5 pl-6 opacity-80",
    bottomDiv: "flex border-t border-gray-100 pt-4 mt-3 leading-5",
    tipMethodDesc: "mt-1 text-12 opacity-80",
    checkboxRowItem: "h-10 pt-1.5",
    percentageRowItem: "h-10",
    percentageInput: "mb-0 w-[12rem]",
    inputsTitle: "mb-2 text-13 font-bold",
    error: "align-right text-13 text-red-500",
}
